import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getCacheStats, flushCache, type CacheStats } from '../api/cache';
import { StatusBadge } from './common/StatusBadge';
import { ConfirmDialog } from './common/ConfirmDialog';

export function CacheView() {
  const { getJwtToken } = useAuth();

  const [stats, setStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFlushConfirm, setShowFlushConfirm] = useState(false);
  const [flushing, setFlushing] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await getCacheStats(token);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cache stats');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleFlush = async () => {
    setFlushing(true);
    try {
      const token = await getJwtToken();
      if (!token) return;
      await flushCache(token);
      setShowFlushConfirm(false);
      await fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to flush cache');
      setShowFlushConfirm(false);
    } finally {
      setFlushing(false);
    }
  };

  if (loading && !stats) {
    return <div className="view__empty">Loading cache stats...</div>;
  }

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Cache</h1>
        <div className="view__actions">
          <button
            className="btn btn--danger"
            onClick={() => setShowFlushConfirm(true)}
            disabled={flushing}
            type="button"
          >
            Flush Cache
          </button>
          <button
            className="btn btn--secondary"
            onClick={fetchStats}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      <section className="view__section">
        <h2 className="view__section-title">Cache Status</h2>
        <div className="settings-card">
          <div className="settings-card__row">
            <span className="settings-card__label">Status</span>
            <StatusBadge
              status={stats?.status === 'healthy' || stats?.status === 'connected' ? 'Active' : 'Disabled'}
            />
          </div>
          {stats?.cache_type && (
            <div className="settings-card__row">
              <span className="settings-card__label">Cache Type</span>
              <span className="settings-card__value">{String(stats.cache_type)}</span>
            </div>
          )}
          {stats?.healthy !== undefined && (
            <div className="settings-card__row">
              <span className="settings-card__label">Healthy</span>
              <span className="settings-card__value">{stats.healthy ? 'Yes' : 'No'}</span>
            </div>
          )}
        </div>
      </section>

      {/* Show any additional stats from the response */}
      {stats && Object.keys(stats).filter(
        (k) => !['status', 'cache_type', 'healthy', 'error'].includes(k),
      ).length > 0 && (
        <section className="view__section">
          <h2 className="view__section-title">Statistics</h2>
          <div className="settings-card">
            {Object.entries(stats)
              .filter(([k]) => !['status', 'cache_type', 'healthy', 'error'].includes(k))
              .map(([key, value]) => (
                <div key={key} className="settings-card__row">
                  <span className="settings-card__label">{key}</span>
                  <span className="settings-card__value">{String(value ?? '--')}</span>
                </div>
              ))}
          </div>
        </section>
      )}

      <ConfirmDialog
        isOpen={showFlushConfirm}
        title="Flush Cache"
        message="Are you sure you want to flush the entire cache? This will clear all cached responses and may temporarily increase API latency."
        confirmLabel="Flush"
        variant="danger"
        onConfirm={handleFlush}
        onCancel={() => setShowFlushConfirm(false)}
      />
    </div>
  );
}
